import { type PointerEvent, useEffect, useRef, useState } from "react";
import { GrowText } from "../../components/ui/GrowText";
import { Icon } from "../../components/ui/Icon";

/** A calm speaking pace: about 150 words a minute. */
export const WORDS_A_SECOND = 2.5;
const SHORTEST = 15;
const LONGEST = 30;

export const scriptLines = (says: string) =>
  says
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
const secondsOf = (line: string) => line.split(/\s+/).filter(Boolean).length / WORDS_A_SECOND;
const moved = (ls: string[], from: number, to: number) => {
  const next = [...ls];
  const [l] = next.splice(from, 1);
  next.splice(to, 0, l);
  return next;
};

/** How long the script runs and whether that fits the ad's 15 to 30 seconds. */
export function fitOf(lines: string[]): { seconds: number; words: string; over: boolean } {
  const seconds = Math.round(lines.reduce((t, l) => t + secondsOf(l), 0));
  if (seconds > LONGEST) return { seconds, words: `About ${seconds} seconds: ${seconds - LONGEST} over, trim a line`, over: true };
  if (seconds < SHORTEST) return { seconds, words: `About ${seconds} seconds: short of ${SHORTEST}`, over: false };
  return { seconds, words: `About ${seconds} seconds: fits ${SHORTEST} to ${LONGEST}`, over: false };
}

/**
 * The script to read on camera, one spoken line per row with its seconds, timed against the ad's 15 to 30
 * seconds: read it full screen (and record), copy it, or change a line.
 */
export function ScriptLines({ says, busy, onSave, onRead }: { says: string; busy: boolean; onSave: (says: string) => void; onRead: (lines: string[]) => void }) {
  const [lines, setLines] = useState(() => scriptLines(says));
  const [copied, setCopied] = useState(false);
  const list = useRef<HTMLOListElement>(null);
  const grips = useRef<(HTMLButtonElement | null)[]>([]);
  const drag = useRef<{ from: number; to: number; y0: number; rects: DOMRect[] } | null>(null);
  useEffect(() => setLines(scriptLines(says)), [says]);
  const fit = fitOf(lines);
  const keep = (next: string[]) => {
    const clean = next.map((l) => l.trim()).filter(Boolean);
    setLines(clean);
    if (clean.join("\n") !== scriptLines(says).join("\n")) onSave(clean.join("\n"));
  };
  // --- Moving a line by its handle: the rows slide while it is dragged, and the order is saved on drop ---
  const rows = () => [...(list.current?.children ?? [])] as HTMLElement[];
  const follow = (y: number) => {
    const d = drag.current;
    if (!d) return;
    const { from, rects } = d;
    const dy = Math.max(rects[0].top - rects[from].top, Math.min(rects[rects.length - 1].bottom - rects[from].bottom, y - d.y0));
    // Why: a row gives way once the dragged one's edge passes its middle.
    const top = rects[from].top + dy;
    const bottom = rects[from].bottom + dy;
    let to = from;
    rects.forEach((r, k) => {
      const c = r.top + r.height / 2;
      if (k < from && top < c) to = Math.min(to, k);
      if (k > from && bottom > c) to = Math.max(to, k);
    });
    d.to = to;
    const h = rects[from].height;
    rows().forEach((x, k) => {
      const shift = k === from ? dy : from < to && k > from && k <= to ? -h : from > to && k >= to && k < from ? h : 0;
      x.style.transform = shift ? `translateY(${shift}px)` : "";
    });
  };
  const drop = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    // Why: the sliding stops before the rows go back in place, so they do not glide back after the drop.
    list.current?.classList.remove("dragging");
    for (const x of rows()) {
      x.style.transform = "";
      x.classList.remove("lifted");
    }
    if (d.to !== d.from) keep(moved(lines, d.from, d.to));
  };
  const lift = (i: number) => (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const items = rows();
    drag.current = { from: i, to: i, y0: e.clientY, rects: items.map((x) => x.getBoundingClientRect()) };
    items[i].classList.add("lifted");
    list.current?.classList.add("dragging");
    // Why: followed on the window, so the drag goes on wherever the pointer is.
    const move = (m: globalThis.PointerEvent) => follow(m.clientY);
    const end = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", end);
      removeEventListener("pointercancel", end);
      drop();
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", end);
    addEventListener("pointercancel", end);
  };
  // Why: by keyboard a line moves in place and keeps focus; it is saved when focus leaves the script.
  const step = (i: number, by: number) => {
    const to = i + by;
    if (to < 0 || to >= lines.length) return;
    setLines((ls) => moved(ls, i, to));
    requestAnimationFrame(() => grips.current[to]?.focus());
  };
  const copy = () =>
    navigator.clipboard?.writeText(lines.join("\n")).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  return (
    <div className="spt">
      <div className="spt-h">
        <span className="spt-t">
          <b>Your script</b>
          <span className={fit.over ? "spt-fit over" : "spt-fit"}>
            <i />
            {fit.words}
          </span>
        </span>
        <span className="spt-acts">
          <button className="btn sm" type="button" disabled={busy || !lines.length} onClick={() => onRead(lines.map((l) => l.trim()).filter(Boolean))}>
            <Icon name="play" />
            Read it
          </button>
          <button className="btn sm ghost" type="button" disabled={!lines.length} onClick={() => void copy()}>
            {copied ? "Copied" : "Copy"}
          </button>
        </span>
      </div>
      <div className={fit.over ? "spt-meter over" : "spt-meter"} aria-hidden="true">
        <span className="band" />
        <span className="fill" style={{ width: `${Math.min(100, (fit.seconds / 45) * 100)}%` }} />
      </div>
      <div className="spt-scale" aria-hidden="true">
        <span>0s</span>
        <span>15s</span>
        <span>30s</span>
        <span>45s</span>
      </div>
      {/* Why: saved once when focus leaves the script; a save per line would lock the next line mid-typing. */}
      <ol
        ref={list}
        className="spt-cues"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) keep(lines);
        }}
      >
        {lines.map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a line's place is its identity; its words change as it is typed.
          <li key={i} className="spt-cue">
            <button
              className="grip"
              type="button"
              aria-label={`Move line ${i + 1}, or use the up and down arrows`}
              disabled={busy}
              ref={(el) => {
                grips.current[i] = el;
              }}
              onPointerDown={lift(i)}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                  step(i, e.key === "ArrowUp" ? -1 : 1);
                }
              }}
            >
              <Icon name="grip" />
            </button>
            <span className="n">{i + 1}</span>
            <GrowText
              rows={1}
              aria-label={`Line ${i + 1}`}
              autoFocus={l === "" && i === lines.length - 1}
              value={l}
              disabled={busy}
              onChange={(e) => setLines((ls) => ls.map((x, k) => (k === i ? e.target.value.replace(/\n/g, " ") : x)))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
            <span className="s">{Math.max(1, Math.round(secondsOf(l)))}s</span>
          </li>
        ))}
      </ol>
      <button className="add" type="button" disabled={busy} onMouseDown={(e) => e.preventDefault()} onClick={() => setLines((ls) => [...ls, ""])}>
        <Icon name="plus" />
        Add a line
      </button>
    </div>
  );
}
